import {
  Divider,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { listCategoriesAll } from '../../api/products';
import useAuthToken from '../../hooks/getUserData';
import { badgeOptions } from '../../staticData/common';
import CustomButton from '../elements/CustomButton';
import Dropdown2 from '../elements/Dropdown2';
import InputField from '../elements/InputField';

const ProductForm = ({ onSubmit, product }) => {
  const [categories, setCategories] = useState([]);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const token = useAuthToken();

  useEffect(() => {
    async function fetchCustomerData() {
      try {
        const res = await listCategoriesAll(token);
        if (res.ok) {
          const data = await res.json();
          const categoryData = Array.isArray(data?.categories)
            ? data.categories.map((category) => ({
                value: category.id || '',
                label: category.name || '',
                details: category.category_details || [],
              }))
            : [];

          setCategories(categoryData);
        } else {
          throw new Error('Failed to fetch customers');
        }
      } catch (error) {
        console.error('Error fetching customers list:', error);
      }
    }
    fetchCustomerData();
  }, [token]);

  const handleCategoryChange = (e) => {
    const selectedCategory = categories.find(
      (category) => category.value === e.target.value
    );
    setCategoryDetails(selectedCategory ? selectedCategory.details || [] : []);
  };

  const categoryProductRelation = categoryDetails.reduce((acc, detail) => {
    const detailKey = `${detail}`;
    acc[detailKey] = product?.categoryProductRelation?.[detailKey] || '';
    return acc;
  }, {});

  return (
    <>
      <Formik
        initialValues={{
          title: product?.title || '',
          price: product?.price || '',
          description: product?.description || '',
          category: product?.category || '',
          brand: product?.brand || '',
          badge: product?.badge === 'None' ? null : product?.badge || null,
          categoryProductRelation: categoryProductRelation || {},
        }}
        validationSchema={Yup.object().shape({
          title: Yup.string()
          .matches(
            /^[\s\S]{1,255}$/, 
            'Product Name should not exceed 255 characters'
          )
          .required('Product Name is required'),
          price: Yup.string()
            .matches(/^\$?[\d,]+(\.\d{1,2})?$/, 'Invalid price format')
            .required('Product Price is required'),
          description: Yup.string().max(700),
          category: Yup.string().required('Category is required'),
          brand: Yup.string().required('Brand is required'),
        })}
        onSubmit={async (values) => {
          categoryDetails.forEach((detail) => {
            const detailKey = `${detail}`;
            if (!values.categoryProductRelation.hasOwnProperty(detailKey)) {
              values.categoryProductRelation[detailKey] = '';
            }
          });
          await onSubmit(values);
        }}>
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Product Name"
                    id="title"
                    type="title"
                    value={values.title}
                    name="title"
                    fullWidth
                    placeholder="Product Name"
                    error={Boolean(touched.title && errors.title)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="category"
                    selected={values.category}
                    handleChange={(e) => {
                      handleChange(e);
                      handleCategoryChange(e);
                    }}
                    elementLabel="Category"
                    options={categories}
                    required
                    error={Boolean(touched.category && errors.category)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Brand"
                    id="brand"
                    type="brand"
                    value={values.brand}
                    name="brand"
                    fullWidth
                    placeholder="Brand"
                    error={Boolean(touched.brand && errors.brand)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Product Price (approx.)"
                    id="price"
                    type="text"
                    value={`$${values.price}`}
                    name="price"
                    fullWidth
                    required
                    placeholder="Product Price"
                    error={Boolean(touched.price && errors.price)}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const formattedPrice = e.target.value.replace('$', '');
                      handleChange({
                        target: {
                          name: 'price',
                          value: formattedPrice,
                        },
                      });
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="badge"
                    selected={values.badge}
                    handleChange={handleChange}
                    elementLabel="Badge (Optional)"
                    options={badgeOptions}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Description"
                    id="description"
                    type="text"
                    value={values.description}
                    name="description"
                    fullWidth
                    rows={5}
                    multiline
                    placeholder="Enter product description"
                    error={Boolean(touched.description && errors.description)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
              {categoryDetails?.length > 0 && (
                <Grid item xs={12}>
                  <Divider />
                  <Typography variant="h6">
                    Product Comparison Details
                  </Typography>
                </Grid>
              )}
              {categoryDetails.map((detail, index) => (
                <Grid item xs={12} key={index}>
                  <Stack spacing={1}>
                    <InputField
                      label={`${detail
                        .split('_')
                        .map((a) => a.charAt(0).toUpperCase() + a.slice(1))
                        .join('')}`}
                      id={`categoryProductRelation.${detail}`}
                      type="text"
                      value={values[`categoryProductRelation.${detail}`]}
                      name={`categoryProductRelation.${detail}`}
                      fullWidth
                      placeholder={`Enter ${detail
                        .split('_')
                        .map((a) => a.charAt(0).toUpperCase() + a.slice(1))
                        .join('')}`}
                      error={Boolean(
                        touched[`categoryProductRelation.${detail}`] &&
                          errors[`categoryProductRelation.${detail}`]
                      )}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      multiline
                    />
                  </Stack>
                </Grid>
              ))}
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <CustomButton />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ProductForm;
